import { useEffect, useState } from 'react';

import {
  Badge,
  Card,
  IconButton,
  LineChart,
  List,
  ListSkeleton,
  OffsetPagination,
  Text,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@opositandopn/ui';
import {
  IoArrowForwardCircleOutline,
  IoClipboardOutline,
} from 'react-icons/io5';
import { useWindowSize } from 'usehooks-ts';

import { ResourceDialog } from '../../../../components/shared/resource-dialog/ResourceDialog';
import { useIsPremium } from '../../../../context/ProfileContext';
import { useAppActions } from '../../../../state/appStore';
import { BaseComponentProps } from '../../../../types/base-component.types';
import { formatSortDayDate } from '../../../../utils/date';
import { useGetMultipleChoiceAssessments } from '../../api/useMultipleChoiceAssessments';
import { AssessmentListFilterForm } from '../assessment-list-filter/AssessmentListFilter';
import { MultipleChoiceAssessmentPreview } from '../multiple-choice-assessment-preview/MultipleChoiceAssessmentPreview';

import styles from './MultipleChoiceAssessmentList.module.scss';

type ContainerProps = BaseComponentProps & {
  filter: AssessmentListFilterForm;
  onSelected?: (assessmentId: string) => void;
};

function MultipleChoiceAssessmentList({
  className,
  filter,
  onSelected,
  testId = 'ui-multiple-choice-assessment-list',
}: ContainerProps) {
  const isPremium = useIsPremium();
  const { setShowGetPremiumPopup } = useAppActions();
  const { width } = useWindowSize();

  const [current, setCurrent] = useState<string>();
  const [pagination, setPagination] = useState({ pageNumber: 1, pageSize: 20 });

  const { data: response, isLoading } = useGetMultipleChoiceAssessments({
    subjectId: filter.subject.id,
    pageNumber: pagination.pageNumber,
    pageSize: pagination.pageSize,
    fromTime: filter.fromTime,
    toTime: filter.toTime,
    sort: filter.sort,
    params: {
      text: filter.text,
      statuses: filter.statuses,
      visibilities: filter.visibilities,
      purposes: filter.purposes,
      referenceStandards: filter.referenceStandards,
      methodologies: filter.methodologies,
      perspectives: filter.perspectives,
      settings: filter.settings,
      difficultyLevels: filter.difficultyLevels,
      tagIds: filter.tags?.map((tag) => tag.id),
      questionIds: filter.questionIds,
      numberOfChoices: filter.numberOfChoices,
      official: filter.official,
      released: filter.released,
    },
  });

  useEffect(() => {
    setPagination({ ...pagination, pageNumber: 1 });
  }, [filter]);

  return (
    <>
      <Card
        title="Simulacros"
        caption={
          response?.totalItems ? `Total: ${response?.totalItems}` : undefined
        }
        className={className}
        testId={testId}
      >
        {isLoading || !response ? (
          <ListSkeleton items={pagination.pageSize} />
        ) : (
          <>
            <List
              items={response.items.map((item) => ({
                text:
                  item.name.length > 120
                    ? item.name.substring(0, 120) + '…'
                    : item.name,
                info: item.name,
                icon: (
                  <>
                    <Text
                      variety="annotation"
                      font="monospace"
                      style={{ marginRight: '5px' }}
                    >
                      {width > 576
                        ? `${item.questions.length} preguntas`
                        : `${item.questions.length} preg.`}
                    </Text>
                    {item.attemptScores && item.attemptScores.length > 0 && (
                      <Tooltip placement="left">
                        <TooltipTrigger asChild={true}>
                          <Badge bg="featured">
                            {
                              item.attemptScores[item.attemptScores.length - 1]
                                .score
                            }
                          </Badge>
                        </TooltipTrigger>
                        <TooltipContent className={styles['content']}>
                          <LineChart
                            labels={item.attemptScores.map((score) =>
                              formatSortDayDate(new Date(score.date)),
                            )}
                            dataset={[
                              {
                                label: 'Nota',
                                values: item.attemptScores.map(
                                  (score) => score.score,
                                ),
                              },
                            ]}
                          />
                        </TooltipContent>
                      </Tooltip>
                    )}
                    <IconButton
                      style={{
                        opacity:
                          !isPremium && item.visibility === 'private' ? 0.3 : 1,
                      }}
                      size="sm"
                      icon={<IoClipboardOutline />}
                      onClick={() => {
                        if (!isPremium && item.visibility === 'private') {
                          setShowGetPremiumPopup(true);
                          return;
                        }

                        setCurrent(item.id);
                      }}
                    />
                  </>
                ),
              }))}
            />
            <OffsetPagination
              pageNumber={pagination.pageNumber}
              pageSize={pagination.pageSize}
              totalRecords={response.totalItems}
              onPageChange={(pageNumber: number) => {
                setPagination({ ...pagination, pageNumber });
              }}
              pageNeighbours={2}
            />
          </>
        )}
      </Card>
      <ResourceDialog open={current !== undefined}>
        {current && (
          <MultipleChoiceAssessmentPreview
            subjectId={filter.subject.id}
            assessmentId={current}
            onClose={() => {
              setCurrent(undefined);
            }}
          >
            {onSelected && (
              <IconButton
                icon={<IoArrowForwardCircleOutline />}
                onClick={() => {
                  onSelected(current);
                }}
              />
            )}
          </MultipleChoiceAssessmentPreview>
        )}
      </ResourceDialog>
    </>
  );
}

export { MultipleChoiceAssessmentList };
